import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button, DeliverablePillSummary } from "@shared/ui";
import { Avatar, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import styles from "./PurchasePreCheckModal.module.scss";

export interface InvalidInfluencer extends IBriefInfluencerV2 {
  reason: string;
}

// ─── Valid Creators Table ───────────────────────────────────────────────────

interface ValidCreatorsTableProps {
  creators: IBriefInfluencerV2[];
  onRemoveCreator: (id: string) => void;
}

function ValidCreatorsTable({
  creators,
  onRemoveCreator,
}: ValidCreatorsTableProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleCreators = isExpanded ? creators : creators.slice(0, 5);

  const formatPrice = (influencer: IBriefInfluencerV2): string => {
    const cost = influencer.brandCost?.cost;
    if (cost === undefined || cost === null) return "N/A";
    return `₹${Number(cost).toLocaleString("en-IN")}`;
  };

  const columns: ColumnsType<IBriefInfluencerV2> = [
    {
      title: "Creator",
      key: "creator",
      width: "25%",
      render: (_: unknown, record: IBriefInfluencerV2) => (
        <div className={styles.creatorCell}>
          <Avatar size={28} src={record.socialMedia?.profilePicUrl}>
            {record.influencerName?.[0]}
          </Avatar>
          <span className={styles.creatorName}>
            {record.socialMedia?.username || record.influencerName}
          </span>
        </div>
      ),
    },
    {
      title: "Deliverables",
      key: "deliverables",
      width: "40%",
      render: (_: unknown, record: IBriefInfluencerV2) => (
        <div className={styles.deliverablesCell}>
          {record?.deliverableV2 ? (
            <DeliverablePillSummary deliverable={record.deliverableV2} />
          ) : (
            <span className={styles.noDeliverables}>No Deliverables</span>
          )}
        </div>
      ),
    },
    // {
    //   title: "Ad Rights",
    //   key: "adRights",
    //   width: "15%",
    //   render: (_: unknown, record: IBriefInfluencerV2) => (
    //     <div>
    //       {record.deliverableV2?.purchaseItems?.map((del, idx) =>
    //         del.purchaseItemType === "Ad-Rights" ? (
    //           <Tooltip title={del.adRightsDescription} key={del._id || idx}>
    //             <Tag style={{ fontSize: 11 }}>{`${del.adRightsType}-${del.adRightsDurations}`}</Tag>
    //           </Tooltip>
    //         ) : null
    //       )}
    //     </div>
    //   ),
    // },
    {
      title: "Price",
      key: "price",
      width: "12%",
      align: "right",
      render: (_: unknown, record: IBriefInfluencerV2) => (
        <span className={styles.priceCell}>{formatPrice(record)}</span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: "8%",
      align: "right",
      render: (_: unknown, record: IBriefInfluencerV2) => (
        <button
          onClick={() => onRemoveCreator(record._id)}
          className={styles.removeButton}
          title="Remove from purchase"
        >
          <Trash2 size={14} />
        </button>
      ),
    },
  ];

  if (creators.length === 0) return null;

  return (
    <div className={styles.validCreatorsSection}>
      <div className={styles.sectionHeader}>
        <div className={`${styles.statusIndicator} ${styles.valid}`} />
        <h4 className={styles.sectionTitle}>
          Ready for Purchase ({creators.length})
        </h4>
      </div>
      <div className={styles.tableWrapper}>
        <Table
          columns={columns}
          dataSource={visibleCreators}
          rowKey="_id"
          pagination={false}
          size="small"
        />
        {creators.length > 5 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={styles.expandButton}
          >
            {isExpanded
              ? "Show Less"
              : `View ${creators.length - 5} more`}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Invalid Creators Table ─────────────────────────────────────────────────

interface InvalidCreatorsTableProps {
  creators: InvalidInfluencer[];
}

function InvalidCreatorsTable({ creators }: InvalidCreatorsTableProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleCreators = isExpanded ? creators : creators.slice(0, 5);

  const columns: ColumnsType<InvalidInfluencer> = [
    {
      title: "Creator",
      key: "creator",
      width: "50%",
      render: (_: unknown, record: InvalidInfluencer) => (
        <div className={`${styles.creatorCell} ${styles.opaque}`}>
          <Avatar
            size={24}
            src={record.socialMedia?.profilePicUrl}
            style={{ filter: "grayscale(100%)" }}
          >
            {record.influencerName?.[0]}
          </Avatar>
          <span className={styles.creatorName}>
            {record.socialMedia?.username || record.influencerName}
          </span>
        </div>
      ),
    },
    {
      title: "Reason",
      key: "reason",
      width: "50%",
      render: (_: unknown, record: InvalidInfluencer) => (
        <span className={styles.reasonText}>{record.reason}</span>
      ),
    },
  ];

  if (creators.length === 0) return null;

  return (
    <div className={styles.invalidCreatorsSection}>
      <div className={styles.sectionHeader}>
        <div className={`${styles.statusIndicator} ${styles.invalid}`} />
        <h4 className={styles.sectionTitle}>Cannot Purchase ({creators.length})</h4>
        <span className={styles.actionBadge}>Action Required</span>
      </div>
      <div className={`${styles.tableWrapper} ${styles.invalidTable}`}>
        <Table
          columns={columns}
          dataSource={visibleCreators}
          rowKey="_id"
          pagination={false}
          size="small"
        />
        {creators.length > 5 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={styles.expandButton}
          >
            {isExpanded
              ? "Show Less"
              : `View ${creators.length - 5} more`}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Purchase PreCheck Modal Content ───────────────────────────────────────

interface PurchasePreCheckModalContentProps {
  validCreators: IBriefInfluencerV2[];
  invalidCreators: InvalidInfluencer[];
  onRemoveCreator: (id: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isPurchasing?: boolean;
}

export function PurchasePreCheckModalContent({
  validCreators,
  invalidCreators,
  onRemoveCreator,
  onCancel,
  onConfirm,
  isPurchasing = false,
}: PurchasePreCheckModalContentProps) {
  const totalAmount = validCreators.reduce(
    (sum, inf) => sum + (inf.brandCost?.cost || 0),
    0
  );
  const isConfirmDisabled = validCreators.length === 0;

  return (
    <div className={styles.content}>
      <div className={styles.body}>
        {validCreators.length === 0 && invalidCreators.length === 0 && (
          <p className={styles.emptyState}>No creators to process.</p>
        )}
        {validCreators.length > 0 && (
          <ValidCreatorsTable
            creators={validCreators}
            onRemoveCreator={onRemoveCreator}
          />
        )}
        {invalidCreators.length > 0 && (
          <InvalidCreatorsTable creators={invalidCreators} />
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <span className={styles.totalLabel}>Total Amount</span>
          <span className={styles.totalAmount}>
            ₹{Math.abs(totalAmount).toLocaleString("en-IN")}
          </span>
        </div>
        <div className={styles.footerActions}>
          <Button onClick={onCancel} type="default">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            type="primary"
            disabled={isConfirmDisabled || isPurchasing}
            loading={isPurchasing}
          >
            Continue{validCreators.length > 0 ? ` (${validCreators.length})` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
